import { useMutation } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { getCashDroBridge, logCashDro } from "../utils/cashDro";
import useCashDroSettingsQuery from "../queries/cashDro/useCashDroSettingsQuery";
import useCashDroSettingsPut from "../mutations/cashDro/useCashDroSettingsPut";
import { CashDroSettings } from "../v2/queries/interfacesV2";

const DEFAULT_CASH_DRO_SETTINGS = {
  ip: "",
  is_active: false,
  password: "",
  pos_name: "",
  user_name: "",
};

const useCashDroConfigHook = () => {
  const bridge = getCashDroBridge();

  const cashDroQuery = useCashDroSettingsQuery();
  const cashDroPut = useCashDroSettingsPut();

  const testConnection = useMutation({
    mutationFn: (settings: CashDroSettings) =>
      bridge.testConnection({
        ip: settings.ip.trim(),
        name: settings.user_name.trim(),
        password: settings.password,
      }),
  });

  return {
    isAvailable: Boolean(bridge),
    isActive: Boolean(
      bridge && cashDroQuery.data?.is_active && cashDroQuery.data?.ip
    ),
    settings: cashDroQuery.data ?? DEFAULT_CASH_DRO_SETTINGS,
    query: cashDroQuery,
    saveSettings: cashDroPut,
    testConnection,
  };
};

/**
 * Hands the branch's settings to the desktop app, which uses them for every CashDro call. Runs once
 * in the desktop app and again whenever the settings or the branch change.
 */
export const useSyncCashDroSettingsToDesktop = () => {
  const { settings } = useCashDroConfigHook();
  const lastSynced = useRef<string>();

  useEffect(() => {
    const bridge = getCashDroBridge();
    if (!bridge || !settings) {
      return;
    }
    const config = {
      enabled: settings.is_active,
      ip: settings.ip,
      name: settings.user_name,
      password: settings.password,
      posId: settings.pos_name,
    };
    const key = JSON.stringify(config);
    if (key === lastSynced.current) {
      return;
    }
    lastSynced.current = key;
    bridge
      .saveConfig(config)
      .then((saved) => logCashDro("settings synced to the desktop app", saved))
      .catch((error) =>
        logCashDro("could not sync settings to the desktop app", String(error))
      );
  }, [settings]);
};

export default useCashDroConfigHook;
